import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { useNavigate } from 'react-router-dom';
import {
  Video, Image, Sparkles, Download, Share2, Upload, Play, Pause,
  RotateCw, Wand2, Palette, Shirt, Camera, Film, Layers, Grid3X3,
  ChevronRight, X, Check, Loader2, RefreshCw, Eye, Zap, Crown,
  CircleDot, Box, Smartphone, Monitor,
  ArrowRight, Clock, Star, Heart, Settings, ChevronLeft, Trash2, ImagePlus, Lock, Bookmark
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../components/ui/use-toast';
import VirtualTryOn from '../components/VirtualTryOn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

// Map feature ID to usage feature name
const FEATURE_USAGE_MAP = {
  'fashion-video': 'runway_videos',
  '360view': 'view_360',
  'background': 'studio_bg_removal',
  'cinematic': 'creative_vision',
  'tryon': 'tryon_standard'
};

// Feature cards for the main menu
const AI_FEATURES = [
  {
    id: 'tryon',
    title: 'Virtual Try-On',
    description: 'Try clothes on yourself using AI-powered fitting',
    icon: Shirt,
    gradient: 'from-amber-500 to-orange-500',
    tag: 'Essential',
    cost: '~$0.20'
  },
  {
    id: 'fashion-video',
    title: 'Fashion Video',
    description: 'Create any fashion video — runway, promo, social content and more',
    icon: Film,
    gradient: 'from-stone-700 to-stone-500',
    tag: 'Popular',
    cost: '~$0.10'
  },
  {
    id: '360view',
    title: '360° View',
    description: 'See your outfit from every angle',
    icon: RotateCw,
    gradient: 'from-terracotta-500 to-terracotta-600',
    tag: 'AI Magic',
    cost: '~$0.30'
  },
  {
    id: 'background',
    title: 'Background',
    description: 'Change or remove backgrounds instantly',
    icon: Layers,
    gradient: 'from-mud-600 to-mud-500',
    tag: 'Essential',
    cost: '~$0.10'
  },
  {
    id: 'cinematic',
    title: 'Creative Vision',
    description: 'Upload your photo and create cinematic, editorial-quality fashion imagery',
    icon: Camera,
    gradient: 'from-amber-500 to-orange-600',
    tag: 'New',
    cost: '~$0.20'
  }
];

// Cinematic image size options
const CINEMATIC_SIZES = [
  { id: '2K', name: '2K', description: 'High quality' },
  { id: '4K', name: '4K', description: 'Ultra high quality' }
];

// Cinematic aspect ratio options
const CINEMATIC_ASPECTS = [
  { id: '16:9', name: '16:9', description: 'Landscape', icon: Monitor },
  { id: '9:16', name: '9:16', description: 'Portrait', icon: Smartphone },
  { id: '1:1', name: '1:1', description: 'Square', icon: Grid3X3 },
  { id: '4:3', name: '4:3', description: 'Classic', icon: Monitor },
  { id: '3:2', name: '3:2', description: 'Photo', icon: Camera }
];

// Aspect ratio options for fashion videos
const ASPECT_RATIOS = [
  { id: '9:16', name: 'Vertical', description: 'TikTok/Reels', icon: Smartphone },
  { id: '16:9', name: 'Horizontal', description: 'YouTube/TV', icon: Monitor },
  { id: '1:1', name: 'Square', description: 'Instagram', icon: Grid3X3 },
  { id: '4:3', name: '4:3', description: 'Classic', icon: Monitor },
  { id: '3:2', name: '3:2', description: 'Photo', icon: Camera }
];

// Duration options (p-video supports 1-10s)
const DURATION_OPTIONS = [
  { id: 3, name: '3 seconds', description: 'Quick clip' },
  { id: 5, name: '5 seconds', description: 'Standard' },
  { id: 8, name: '8 seconds', description: 'Extended' },
  { id: 10, name: '10 seconds', description: 'Full video' }
];

// Background presets — only Remove BG is active for now
// Scene backgrounds (Studio White, Nature, etc.) are archived until a proper compositing model is integrated
const BACKGROUNDS = [
  { id: 'transparent', name: 'Remove BG', preview: '✂️', color: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 16px 16px', description: 'Remove background completely (transparent PNG)' }
];

const AIStudio = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Main state
  const [activeFeature, setActiveFeature] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [endImage, setEndImage] = useState(null); // For end frame
  const [resultImage, setResultImage] = useState(null);
  const [resultVideo, setResultVideo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [videoRotation, setVideoRotation] = useState(0); // 0, 90, 180, 270 degrees
  
  // Usage tracking state
  const [usage, setUsage] = useState(null);
  const [userTier, setUserTier] = useState('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState(null);
  
  // Video generation states (p-video parameters)
  const [videoPrompt, setVideoPrompt] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(5);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('9:16');
  
  // Feature-specific states
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUNDS[0]);
  const [customBgPrompt, setCustomBgPrompt] = useState('');
  // 360 view state
  const [multiImages, setMultiImages] = useState([]);
  const [rotationMode, setRotationMode] = useState('single'); // 'single' = front-to-back, 'dual' = front+back photos
  const [backImage, setBackImage] = useState(null);
  
  // Cinematic image generation states
  const [cinematicPrompt, setCinematicPrompt] = useState('');
  const [cinematicSize, setCinematicSize] = useState('2K');
  const [cinematicAspect, setCinematicAspect] = useState('16:9');
  
  const fileInputRef = useRef(null);
  const endImageInputRef = useRef(null);
  const multiImageInputRef = useRef(null);
  const backImageInputRef = useRef(null);

  // Fetch usage status on mount
  useEffect(() => {
    const fetchUsage = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const res = await axios.get(`${API_URL}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsage(res.data.usage || {});
        setUserTier(res.data.tier || 'free');
      } catch (err) {
        console.error('Failed to fetch usage:', err);
      }
    };
    fetchUsage();
  }, []);

  // Check if user can use a feature
  const canUseFeature = (featureId) => {
    if (!usage) return true; // Allow if usage not loaded yet
    const usageKey = FEATURE_USAGE_MAP[featureId];
    if (!usageKey) return true;
    
    const featureUsage = usage[usageKey];
    if (!featureUsage) return true;
    
    return featureUsage.remaining > 0 || featureUsage.limit === 999;
  };

  // Get remaining uses for a feature
  const getRemainingUses = (featureId) => {
    if (!usage) return null;
    const usageKey = FEATURE_USAGE_MAP[featureId];
    if (!usageKey) return null;
    
    const featureUsage = usage[usageKey];
    if (!featureUsage) return null;
    if (featureUsage.limit === 999) return '∞';
    
    return featureUsage.remaining;
  };

  // Check usage before performing action
  const checkUsageBeforeAction = (featureId) => {
    if (!canUseFeature(featureId)) {
      setBlockedFeature(featureId);
      setShowUpgradeModal(true);
      return false;
    }
    return true;
  };

  // Refresh usage after successful action
  const refreshUsage = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(res.data.usage || {});
    } catch (err) {
      console.error('Failed to refresh usage:', err);
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setResultImage(null);
        setResultVideo(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Simulate progress for long-running tasks
  const simulateProgress = useCallback(() => {
    setProcessingProgress(0);
    const interval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
    return interval;
  }, []);

  // Generate fashion video with p-video
  const generateFashionVideo = async () => {
    // Check usage limit first
    if (!checkUsageBeforeAction('fashion-video')) return;
    
    console.log('🎬 generateFashionVideo called');
    setIsProcessing(true);
    setError(null);
    setResultVideo(null);
    const progressInterval = simulateProgress();
    
    try {
      const prompt = videoPrompt.trim();
      if (!prompt) {
        setError('Please enter a video prompt');
        setIsProcessing(false);
        clearInterval(progressInterval);
        return;
      }
      
      const token = localStorage.getItem('token');
      console.log('📹 Sending request with prompt:', prompt.substring(0, 50) + '...');

      const response = await axios.post(`${API_URL}/content-studio/generate-video`, {
        prompt: prompt,
        image: uploadedImage || null,
        duration: selectedDuration,
        aspectRatio: selectedAspectRatio
      }, { 
        timeout: 600000,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Response received:', response.data);
      setProcessingProgress(100);
      setResultVideo(response.data.videoUrl);
      await refreshUsage();
    } catch (err) {
      console.error('❌ Video generation error:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Video generation is taking longer than expected. The video may still be processing - try refreshing in a minute.');
      } else {
        const rawErr = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to generate video';
        setError(typeof rawErr === 'object' ? (rawErr.message || 'Failed to generate video') : rawErr);
      }
    } finally {
      clearInterval(progressInterval);
      setIsProcessing(false);
    }
  };

  // Handle multi image upload for 360
  const handleMultiImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMultiImages(prev => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMultiImage = (index) => {
    setMultiImages(prev => prev.filter((_, i) => i !== index));
  };

  // Handle back image upload for 360 dual mode
  const handleBackImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setBackImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Generate 360 view
  const generate360View = async () => {
    // Check usage limit first
    if (!checkUsageBeforeAction('360view')) return;
    
    if (!uploadedImage) return;
    
    setIsProcessing(true);
    setError(null);
    const progressInterval = simulateProgress();
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/content-studio/360-view`, {
        imageUrl: uploadedImage,
        endImage: rotationMode === 'dual' ? backImage : null,
        rotationMode: rotationMode
      }, { 
        timeout: 300000,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProcessingProgress(100);
      setResultVideo(response.data.videoUrl);
      await refreshUsage();
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('360° view generation is taking longer than expected. Please try again.');
      } else {
        const rawErr = err.response?.data?.error || err.response?.data?.message || 'Failed to generate 360° view';
        setError(typeof rawErr === 'object' ? (rawErr.message || 'Failed to generate 360° view') : rawErr);
      }
    } finally {
      clearInterval(progressInterval);
      setIsProcessing(false);
    }
  };

  // Change background
  const changeBackground = async () => {
    // Check usage limit first
    if (!checkUsageBeforeAction('background')) return;
    
    if (!uploadedImage) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      // For custom, use the user's typed prompt. For presets, use the ID (which is a descriptive prompt). For transparent, use 'transparent'.
      const bgValue = selectedBackground.id === 'custom' ? customBgPrompt : selectedBackground.id;
      const response = await axios.post(`${API_URL}/content-studio/change-background`, {
        imageUrl: uploadedImage,
        backgroundType: bgValue
      }, { 
        timeout: 180000,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResultImage(response.data.resultUrl || response.data.editedImageUrl);
      await refreshUsage();
    } catch (err) {
      const rawErr = err.response?.data?.error || err.response?.data?.message || 'Failed to change background';
      setError(typeof rawErr === 'object' ? (rawErr.message || 'Failed to change background') : rawErr);
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate cinematic image
  const generateCinematicImage = async () => {
    // Check usage limit first
    if (!checkUsageBeforeAction('cinematic')) return;
    
    if (!cinematicPrompt.trim()) return;
    
    setIsProcessing(true);
    setError(null);
    setResultImage(null);
    const progressInterval = simulateProgress();
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/content-studio/cinematic-image`, {
        prompt: cinematicPrompt,
        size: cinematicSize,
        aspectRatio: cinematicAspect,
        referenceImage: uploadedImage || null
      }, { 
        timeout: 300000,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProcessingProgress(100);
      setResultImage(response.data.imageUrl);
      await refreshUsage();
    } catch (err) {
      const rawErr = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to generate image';
      setError(typeof rawErr === 'object' ? (rawErr.message || 'Failed to generate image') : rawErr);
    } finally {
      clearInterval(progressInterval);
      setIsProcessing(false);
    }
  };

  // Download result to device gallery
  const downloadResult = async () => {
    const url = resultVideo || resultImage;
    if (!url) return;

    const isVid = !!resultVideo;
    const ext = isVid ? 'mp4' : 'png';
    const fileName = `ai-studio-${activeFeature}-${Date.now()}.${ext}`;

    try {
      toast({ title: '⬇️ Downloading...', description: 'Saving to your device' });

      // Check if running in Capacitor (native app)
      const isNative = window.Capacitor?.isNativePlatform?.();

      if (isNative) {
        // Fetch the file as blob then convert to base64
        const response = await fetch(url);
        const blob = await response.blob();
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });

        // Write to device filesystem
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });

        // Share/save — on iOS this opens the share sheet where user can "Save Video/Image"
        await Share.share({
          title: `AI Studio ${activeFeature}`,
          url: savedFile.uri,
        });

        toast({ title: '✅ Ready to save!', description: 'Use the share sheet to save to your Photos' });
      } else {
        // Web fallback — fetch as blob and trigger download
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        toast({ title: '✅ Downloaded!', description: `${fileName} saved` });
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast({ title: '⚠️ Download failed', description: 'Please try again or take a screenshot', variant: 'destructive' });
    }
  };

  // Share to social
  const shareResult = async () => {
    const url = resultVideo || resultImage;
    if (url && navigator.share) {
      try {
        await navigator.share({
          title: 'My AI Fashion Creation',
          text: 'Check out what I created with AI Studio!',
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  // Save result to gallery (backend saved items + localStorage)
  const saveToGallery = async () => {
    const mediaUrl = resultImage || resultVideo;
    if (!mediaUrl) return;

    const featureLabels = {
      'fashion-video': 'Fashion Video',
      '360view': '360° View',
      'background': 'Background Edit',
      'cinematic': 'Cinematic Image'
    };
    const label = featureLabels[activeFeature] || activeFeature || 'Creation';
    const timestamp = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    const uniqueName = `${label} — ${timestamp}`;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API_URL}/saved`, {
          product: {
            name: uniqueName,
            brand: 'AI Studio',
            image_url: mediaUrl,
            store_url: '',
            category: 'AI Studio',
            style: activeFeature || 'ai-generated'
          },
          source: 'ai-studio'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast({ title: '✅ Saved!', description: `${label} added to your Saved ❤️ page → AI Studio section` });
    } catch (e) {
      console.error('Save failed:', e);
      toast({ title: '❌ Could not save', description: e.response?.data?.message || 'Please try again', variant: 'destructive' });
    }
  };

  // Reset and go back
  const goBack = () => {
    setActiveFeature(null);
    setResultImage(null);
    setResultVideo(null);
    setError(null);
    setProcessingProgress(0);
    setMultiImages([]);
    window.scrollTo(0, 0);
  };

  // Render main menu
  const renderMainMenu = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-stone-500/20 to-suede-500/20 rounded-full mb-4"
        >
          <Sparkles className="w-4 h-4 text-suede-400" />
          <span className="text-sm font-medium text-suede-300">Powered by AI</span>
        </motion.div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
          AI Studio
        </h1>
        <p className="text-gray-400 max-w-md mx-auto">
          Transform your fashion photos into stunning videos, 360° views, and more
        </p>
      </div>

      {/* Feature Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {AI_FEATURES.map((feature, index) => {
          const remaining = getRemainingUses(feature.id);
          const canUse = canUseFeature(feature.id);
          
          return (
          <motion.div
            key={feature.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => { setActiveFeature(feature.id); window.scrollTo(0, 0); }}
            className={`group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border cursor-pointer transition-all hover:scale-[1.02] ${
              canUse 
                ? 'border-gray-700/50 hover:border-gray-600' 
                : 'border-red-900/30 opacity-75'
            }`}
          >
            {/* Tag */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {!canUse && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-500/20 text-red-400 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Limit reached
                </span>
              )}
              {canUse && remaining !== null && remaining !== 'unlimited' && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-700/50 text-gray-300">
                  {remaining} left
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-1 rounded-full bg-gradient-to-r ${feature.gradient} text-white`}>
                {feature.tag}
              </span>
            </div>

            {/* Icon */}
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${!canUse ? 'opacity-50' : ''}`}>
              <feature.icon className="w-7 h-7 text-white" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
            <p className="text-sm text-gray-400 mb-4">{feature.description}</p>

            {/* Footer */}
            <div className="flex items-center justify-end">
              {canUse ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
              ) : (
                <span className="text-xs text-yellow-500 flex items-center gap-1">
                  <Crown className="w-4 h-4" />
                  Upgrade to unlock
                </span>
              )}
            </div>
          </motion.div>
          );
        })}
      </div>
    </div>
  );

  // Render upload section
  const renderUploadSection = () => (
    <div className="mb-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      
      {!uploadedImage ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-600 rounded-2xl p-12 text-center cursor-pointer hover:border-suede-500/50 transition-colors"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-stone-500/20 to-suede-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Upload className="w-10 h-10 text-suede-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Upload Your Image</h3>
          <p className="text-gray-400 text-sm">Drag and drop or click to select</p>
          <p className="text-gray-500 text-xs mt-2">Supports JPG, PNG, WebP</p>
        </motion.div>
      ) : (
        <div className="relative">
          <img
            src={uploadedImage}
            alt="Uploaded"
            className="w-full max-h-80 object-contain rounded-xl"
          />
          <button
            onClick={() => {
              setUploadedImage(null);
              setResultImage(null);
              setResultVideo(null);
            }}
            className="absolute top-2 right-2 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white hover:bg-white/20 transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Change
          </button>
        </div>
      )}
    </div>
  );

  // Handle end image upload
  const handleEndImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setEndImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Render fashion video feature with p-video controls
  const renderFashionVideoFeature = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={goBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Fashion Video</h2>
          <p className="text-gray-400 text-sm">Create any fashion video — runway, promo, social content and more</p>
        </div>
      </div>

      {/* Prompt Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Video Prompt <span className="text-suede-400">(Describe your video)</span>
        </label>
        <textarea
          value={videoPrompt}
          onChange={(e) => setVideoPrompt(e.target.value)}
          placeholder="A fashion model walks confidently down a runway in an elegant red dress, professional lighting, cinematic"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-suede-500 focus:ring-1 focus:ring-suede-500 resize-none"
          rows={3}
        />
        {/* Tips */}
        <div className="mt-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
          <p className="text-xs font-medium text-suede-400 mb-2">💡 Tips for better results:</p>
          <ul className="text-xs text-gray-400 space-y-1">
            <li>• <span className="text-gray-300">Runway:</span> "model walks down a runway, catwalk stride, fashion show"</li>
            <li>• <span className="text-gray-300">Promo:</span> "cinematic fashion ad, slow motion, dramatic lighting"</li>
            <li>• <span className="text-gray-300">Social:</span> "trendy outfit reveal, dynamic transitions, TikTok style"</li>
            <li>• <span className="text-gray-300">Quality:</span> add "professional lighting, high quality, cinematic"</li>
          </ul>
        </div>
      </div>

      {/* Duration & Aspect Ratio */}
      <div className="grid grid-cols-2 gap-4">
        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Duration
          </label>
          <div className="grid grid-cols-2 gap-2">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setSelectedDuration(opt.id)}
                className={`py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  selectedDuration === opt.id
                    ? 'border-suede-500 bg-suede-500/10 text-white'
                    : 'border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                <div>{opt.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Aspect Ratio
          </label>
          <div className="grid grid-cols-2 gap-2">
            {ASPECT_RATIOS.slice(0, 4).map((ar) => (
              <button
                key={ar.id}
                onClick={() => setSelectedAspectRatio(ar.id)}
                className={`py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  selectedAspectRatio === ar.id
                    ? 'border-suede-500 bg-suede-500/10 text-white'
                    : 'border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                <ar.icon className="w-4 h-4 mx-auto mb-1" />
                <div className="text-xs">{ar.name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Reference Image Upload (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Reference Image <span className="text-gray-500">(Optional — for image-to-video)</span>
        </label>
        {renderUploadSection()}
      </div>

      {/* Generate Button */}
      <button
        onClick={generateFashionVideo}
        disabled={isProcessing || !videoPrompt.trim()}
        className="w-full py-4 bg-gradient-to-r from-stone-700 to-stone-600 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating... {Math.round(processingProgress)}%
          </>
        ) : (
          <>
            <Film className="w-5 h-5" />
            Generate Fashion Video (~1-3 min)
          </>
        )}
      </button>

      {/* Info */}
      <div className="text-xs text-gray-500 text-center">
        Powered by AI • 720p quality • {selectedDuration}s duration
      </div>

      {/* Progress Bar */}
      {isProcessing && (
        <>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-stone-700 to-stone-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${processingProgress}%` }}
            />
          </div>
          <p className="text-xs text-amber-400/80 text-center">⏳ Please don't refresh or go back — generation takes 1-3 minutes</p>
        </>
      )}

      {/* Result */}
      {resultVideo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-xl p-4"
        >
          {/* Rotation Controls */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="text-xs text-gray-400">Rotate:</span>
            <button
              onClick={() => setVideoRotation((prev) => (prev - 90 + 360) % 360)}
              className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              title="Rotate Left"
            >
              <RotateCw className="w-4 h-4 text-white transform -scale-x-100" />
            </button>
            <span className="text-xs text-gray-300 w-12 text-center">{videoRotation}°</span>
            <button
              onClick={() => setVideoRotation((prev) => (prev + 90) % 360)}
              className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              title="Rotate Right"
            >
              <RotateCw className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={() => setVideoRotation(0)}
              className="px-2 py-1 text-xs bg-gray-700 rounded-lg hover:bg-gray-600 text-gray-300 transition-colors"
            >
              Reset
            </button>
          </div>
          {/* Video with rotation */}
          <div className={`overflow-hidden rounded-lg ${videoRotation === 90 || videoRotation === 270 ? 'aspect-[9/16]' : ''}`}>
            <video
              src={resultVideo}
              controls
              autoPlay
              loop
              className="w-full rounded-lg transition-transform duration-300"
              style={{ transform: `rotate(${videoRotation}deg)`, transformOrigin: 'center center' }}
            />
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={downloadResult}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={saveToGallery}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Bookmark className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={shareResult}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </motion.div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
          {error}
        </div>
      )}
    </div>
  );

  // Render 360 view feature
  const render360Feature = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={goBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">360° View</h2>
          <p className="text-gray-400 text-sm">See your outfit from every angle</p>
        </div>
      </div>

      {/* Mode Selection */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-2">Choose mode</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => { setRotationMode('single'); setBackImage(null); }}
            className={`p-3.5 rounded-xl border-2 transition-all text-left ${
              rotationMode === 'single'
                ? 'border-orange-500 bg-orange-500/10'
                : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
            }`}
          >
            <div className="text-xl mb-1">📸</div>
            <p className="text-sm font-medium text-white">One Photo</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Front → Back rotation. Best for avoiding face changes.</p>
          </button>
          <button
            onClick={() => setRotationMode('dual')}
            className={`p-3.5 rounded-xl border-2 transition-all text-left ${
              rotationMode === 'dual'
                ? 'border-orange-500 bg-orange-500/10'
                : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
            }`}
          >
            <div className="text-xl mb-1">📸📸</div>
            <p className="text-sm font-medium text-white">Front + Back</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Upload both angles for the most accurate rotation.</p>
          </button>
        </div>
      </div>

      {/* Front Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {rotationMode === 'dual' ? 'Front Photo' : 'Your Photo'}
        </label>
        {renderUploadSection()}
      </div>

      {/* Back Image Upload (dual mode only) */}
      {rotationMode === 'dual' && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Back Photo</label>
          <input
            ref={backImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleBackImageUpload}
            className="hidden"
          />
          {!backImage ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => backImageInputRef.current?.click()}
              className="border-2 border-dashed border-gray-600 rounded-2xl p-8 text-center cursor-pointer hover:border-orange-500/50 transition-colors"
            >
              <div className="w-14 h-14 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                <ImagePlus className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-gray-300 font-medium">Upload back view</p>
              <p className="text-gray-500 text-xs mt-1">Same person, same outfit, from behind</p>
            </motion.div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden border border-gray-700">
              <img src={backImage} alt="Back view" className="w-full max-h-64 object-contain bg-gray-900" />
              <button
                onClick={() => setBackImage(null)}
                className="absolute top-2 right-2 p-2 bg-black/60 rounded-full hover:bg-red-500/80 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded-lg text-xs text-gray-300">Back view</div>
            </div>
          )}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-4 border border-orange-500/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <RotateCw className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">How it works</h4>
            <p className="text-sm text-gray-400 mt-1">
              {rotationMode === 'single'
                ? 'AI will turn your model from front to back, showing the outfit from all angles without returning to the front — keeping the face consistent.'
                : 'Upload a front and back photo of the same outfit. AI will create a smooth transition between both angles for the most realistic rotation.'
              }
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={generate360View}
        disabled={!uploadedImage || (rotationMode === 'dual' && !backImage) || isProcessing}
        className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating... {Math.round(processingProgress)}%
          </>
        ) : (
          <>
            <RotateCw className="w-5 h-5" />
            Generate 360° View (~2-4 min)
          </>
        )}
      </button>

      {isProcessing && (
        <>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${processingProgress}%` }}
            />
          </div>
          <p className="text-xs text-amber-400/80 text-center">⏳ Please don't refresh or go back — generation takes 2-4 minutes</p>
        </>
      )}

      {resultVideo && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-800 rounded-xl p-4">
          <video src={resultVideo} controls autoPlay loop className="w-full rounded-lg" />
          <div className="flex gap-3 mt-4">
            <button onClick={downloadResult} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={saveToGallery} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Bookmark className="w-4 h-4" /> Save
            </button>
            <button onClick={shareResult} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </motion.div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{error}</div>}
    </div>
  );

  // Render background feature
  const renderBackgroundFeature = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={goBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Background</h2>
          <p className="text-gray-400 text-sm">Change or remove backgrounds</p>
        </div>
      </div>

      {renderUploadSection()}

      {/* Background Selection */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-2">What would you like to do?</h3>
        <p className="text-xs text-gray-500 mb-3">Remove your background or replace it with a new scene</p>
        <div className="grid grid-cols-3 gap-2.5">
          {BACKGROUNDS.map((bg) => (
            <button
              key={bg.id}
              onClick={() => setSelectedBackground(bg)}
              className={`rounded-xl border-2 transition-all p-2.5 flex flex-col items-center text-center ${
                selectedBackground.id === bg.id
                  ? 'border-suede-500 bg-suede-500/10 scale-[1.02]'
                  : 'border-gray-700/60 hover:border-gray-500 bg-gray-800/40'
              }`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-1.5" style={{ background: bg.color }}>
                <span className="text-xl">{bg.preview}</span>
              </div>
              <span className="text-xs font-medium text-gray-200 leading-tight">{bg.name}</span>
              <span className="text-[10px] text-gray-500 leading-tight mt-0.5">{bg.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Background Input */}
      {selectedBackground.id === 'custom' && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-300">Describe your background</h3>
          <textarea
            value={customBgPrompt}
            onChange={(e) => setCustomBgPrompt(e.target.value)}
            placeholder="e.g. Parisian café terrace at sunset, warm golden light, cobblestone street..."
            className="w-full bg-gray-800/50 border border-gray-700 rounded-xl p-3 text-white text-sm placeholder-gray-500 resize-none focus:outline-none focus:border-suede-500 transition-colors"
            rows={3}
          />
          <p className="text-[10px] text-gray-500">Tip: Be descriptive — mention the setting, lighting, mood, and colours you want</p>
        </div>
      )}

      <button
        onClick={changeBackground}
        disabled={!uploadedImage || isProcessing || (selectedBackground.id === 'custom' && !customBgPrompt.trim())}
        className="w-full py-4 bg-gradient-to-r from-mud-600 to-mud-500 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {selectedBackground.id === 'transparent' ? 'Removing Background...' : 'Changing Background...'}
          </>
        ) : (
          <>
            <Layers className="w-5 h-5" />
            {selectedBackground.id === 'transparent' ? 'Remove Background' : 'Apply Background'}
          </>
        )}
      </button>

      {isProcessing && (
        <p className="text-xs text-amber-400/80 text-center">⏳ Please don't refresh or go back — this takes less than a minute</p>
      )}

      {resultImage && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <img src={resultImage} alt="Result" className="w-full rounded-xl" />
          <div className="flex gap-3">
            <button onClick={downloadResult} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={saveToGallery} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Bookmark className="w-4 h-4" /> Save
            </button>
            <button onClick={shareResult} className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </motion.div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{error}</div>}
    </div>
  );

  // Render cinematic image feature
  const renderCinematicFeature = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={goBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Creative Vision</h2>
          <p className="text-gray-400 text-sm">Upload your photo and create cinematic, editorial-quality fashion imagery</p>
        </div>
      </div>

      {/* Hero Description */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-600/10 border border-amber-500/20 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-200 mb-1">How it works</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Upload a picture of yourself, your outfit, or any fashion inspiration — then describe the look you want.
              AI will generate cinematic, film-quality images with stunning aesthetics, perfect lighting, and editorial polish.
            </p>
          </div>
        </div>
      </div>

      {/* Photo Upload */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">Your Photo <span className="text-gray-500">(recommended for best results)</span></h3>
        {renderUploadSection()}
      </div>

      {/* Prompt Input */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">Describe Your Look</h3>
        <textarea
          value={cinematicPrompt}
          onChange={(e) => setCinematicPrompt(e.target.value)}
          placeholder={uploadedImage
            ? "Describe the cinematic style you want — e.g., 'Editorial photoshoot in a Parisian café, golden afternoon light, film grain, warm earth tones, elegant and refined...'"
            : "Describe the fashion image you want to create — e.g., 'A confident woman in an elegant evening gown, walking through a grand marble lobby, cinematic lighting, soft bokeh...'"
          }
          className="w-full h-28 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm resize-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
        />
        <div className="flex justify-between mt-2">
          <p className="text-xs text-gray-500">
            {uploadedImage
              ? 'Tip: Describe the setting, lighting, and mood — your photo guides the style'
              : 'Tip: Include outfit details, setting, lighting, and mood for best results'
            }
          </p>
          <span className="text-xs text-gray-500">{cinematicPrompt.length} chars</span>
        </div>
      </div>

      {/* Quick Fashion Styles */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">Fashion Styles</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Editorial Cover', prompt: 'Ultra-realistic high-fashion editorial magazine cover photo, shot on Canon EOS R5 with 85mm f/1.4 lens, natural skin texture with visible pores and fine details, authentic facial features preserved perfectly, professional studio lighting with soft key light and subtle fill, true-to-life skin tones, no retouching or smoothing on face, sharp focus on eyes, real fabric textures, photorealistic 8K quality' },
            { label: 'Golden Hour Outfit', prompt: 'Ultra-realistic fashion portrait taken during golden hour with natural sunlight, shot on Sony A7IV with 70-200mm lens, authentic skin with natural highlights and warm tones, real sun flare and genuine warm shadows on face, unedited facial proportions, true-to-life hair detail, natural outdoor environment with real bokeh, real clothing wrinkles and fabric weight, photojournalistic fashion quality, no artificial enhancement' },
            { label: 'Dark Luxury', prompt: 'Ultra-realistic dark luxury fashion photograph, shot on Hasselblad medium format camera, dramatic chiaroscuro lighting with single hard light source, authentic skin texture under high contrast, real shadows defining natural bone structure, genuine designer fabric with visible stitching and material quality, unretouched natural features, deep blacks with real detail, true-to-life color accuracy, photorealistic editorial quality' },
            { label: 'Soft Glam', prompt: 'Ultra-realistic soft glamour fashion portrait with natural window light, shot on Nikon Z9 with 50mm f/1.2 lens, genuine skin glow without artificial smoothing, real makeup application with visible product texture, authentic facial expressions and natural eye reflections, true pastel tones from real environment, soft fabric draping naturally on body, no digital alteration of features, photorealistic beauty photography' },
            { label: 'Street Style', prompt: 'Ultra-realistic street style fashion photograph taken candidly in a real city environment, shot on Leica Q3 with natural available light, authentic urban textures and real architecture, genuine clothing fit and natural body posture, unposed natural expression, real pavement reflections, true-to-life weather lighting, actual street atmosphere with depth, no face modification or digital enhancement, documentary fashion photography style' },
            { label: 'Runway Moment', prompt: 'Ultra-realistic fashion runway photograph, shot on Canon EOS R3 with 70-200mm f/2.8 lens, authentic catwalk lighting hitting real skin, genuine fabric movement captured at high shutter speed, natural model stride with real body mechanics, true-to-life audience bokeh in background, visible textile details and garment construction, no facial smoothing or body alteration, sharp editorial press photography quality' },
            { label: 'Lifestyle Chic', prompt: 'Ultra-realistic lifestyle fashion photograph in a genuine cafe or boutique interior, shot on Fujifilm X-T5 with 35mm lens, natural ambient interior lighting with real window light, authentic relaxed pose and genuine facial expression, true-to-life interior colors and textures, real coffee cup and props, natural clothing drape and casual fit, unretouched skin with natural indoor tones, candid photojournalistic fashion quality' },
            { label: 'Evening Elegance', prompt: 'Ultra-realistic evening formal fashion portrait, shot on Sony A1 with 135mm f/1.8 lens, genuine ambient evening lighting with real warm artificial light sources, authentic formal wear fabric sheen and weight, natural skin under mixed lighting, real jewelry reflections, true-to-life evening atmosphere, unmodified facial features with natural evening makeup, real venue background with genuine bokeh, photorealistic high-end event photography' }
          ].map((style) => (
            <button
              key={style.label}
              onClick={() => setCinematicPrompt(style.prompt)}
              className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 hover:border-amber-500/50 hover:text-amber-300 transition-all"
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Size & Aspect Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Size Selection */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-3">Image Quality</h3>
          <div className="grid grid-cols-2 gap-3">
            {CINEMATIC_SIZES.map((size) => (
              <button
                key={size.id}
                onClick={() => setCinematicSize(size.id)}
                className={`p-3 rounded-xl border transition-all text-center ${
                  cinematicSize === size.id
                    ? 'border-amber-500 bg-amber-500/10 text-amber-300'
                    : 'border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                <span className="text-base font-semibold block">{size.name}</span>
                <span className="text-xs opacity-70">{size.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-3">Aspect Ratio</h3>
          <div className="grid grid-cols-5 gap-2">
            {CINEMATIC_ASPECTS.map((ar) => (
              <button
                key={ar.id}
                onClick={() => setCinematicAspect(ar.id)}
                className={`p-2.5 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                  cinematicAspect === ar.id
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <ar.icon className={`w-4 h-4 ${cinematicAspect === ar.id ? 'text-amber-400' : 'text-gray-500'}`} />
                <span className={`text-[11px] font-medium ${cinematicAspect === ar.id ? 'text-amber-300' : 'text-gray-400'}`}>{ar.id}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={generateCinematicImage}
        disabled={isProcessing || !cinematicPrompt.trim()}
        className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Creating Your Vision... {Math.round(processingProgress)}%
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            {uploadedImage ? 'Generate From Your Photo (~1 min)' : 'Generate Fashion Image (~1 min)'}
          </>
        )}
      </button>

      {isProcessing && (
        <p className="text-xs text-amber-400/80 text-center">⏳ Please don't refresh or go back — generation takes less than a minute</p>
      )}

      {/* Features Info */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-gray-800/50 rounded-xl border border-gray-700/50 text-center">
          <Camera className="w-5 h-5 text-amber-400 mx-auto mb-1.5" />
          <span className="text-[11px] font-medium text-gray-300 block">Upload Your Look</span>
          <p className="text-[10px] text-gray-500 mt-0.5">Your photo as the creative base</p>
        </div>
        <div className="p-3 bg-gray-800/50 rounded-xl border border-gray-700/50 text-center">
          <Star className="w-5 h-5 text-amber-400 mx-auto mb-1.5" />
          <span className="text-[11px] font-medium text-gray-300 block">Cinematic Quality</span>
          <p className="text-[10px] text-gray-500 mt-0.5">Film-like lighting & aesthetics</p>
        </div>
        <div className="p-3 bg-gray-800/50 rounded-xl border border-gray-700/50 text-center">
          <Eye className="w-5 h-5 text-amber-400 mx-auto mb-1.5" />
          <span className="text-[11px] font-medium text-gray-300 block">Fashion-Ready</span>
          <p className="text-[10px] text-gray-500 mt-0.5">Editorial & campaign quality</p>
        </div>
      </div>

      {/* Progress Bar */}
      {isProcessing && (
        <div className="w-full bg-gray-700 rounded-full h-2">
          <motion.div
            className="bg-gradient-to-r from-amber-500 to-orange-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${processingProgress}%` }}
          />
        </div>
      )}

      {/* Result */}
      {resultImage && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <img src={resultImage} alt="Fashion creation" className="w-full rounded-xl shadow-2xl" />
          <div className="flex gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Download className="w-4 h-4" /> Download
            </button>
            <button
              onClick={saveToGallery}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Bookmark className="w-4 h-4" /> Save
            </button>
            <button
              onClick={shareResult}
              className="flex-1 py-2 bg-gray-700 rounded-lg text-white flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </motion.div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{error}</div>}
    </div>
  );

  // Render active feature
  const renderActiveFeature = () => {
    switch (activeFeature) {
      case 'tryon':
        return null; // Handled by modal below
      case 'fashion-video':
        return renderFashionVideoFeature();
      case '360view':
        return render360Feature();
      case 'background':
        return renderBackgroundFeature();
      case 'cinematic':
        return renderCinematicFeature();
      default:
        return renderMainMenu();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature || 'menu'}
            initial={{ opacity: 0, x: activeFeature ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: activeFeature ? -20 : 20 }}
            transition={{ duration: 0.2 }}
          >
            {renderActiveFeature()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Virtual Try-On Modal */}
      {activeFeature === 'tryon' && (
        <VirtualTryOn
          onClose={() => setActiveFeature(null)}
        />
      )}
      
      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="w-6 h-6 text-yellow-500" />
              Upgrade Required
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-2">
              {blockedFeature && (
                <>
                  You've reached your monthly limit for{' '}
                  <span className="text-white font-medium">
                    {AI_FEATURES.find(f => f.id === blockedFeature)?.title || blockedFeature}
                  </span>
                  .
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="font-medium text-white mb-2">Your current plan: <span className="capitalize">{userTier}</span></h4>
              <p className="text-sm text-gray-400">
                {userTier === 'free' && 'Upgrade to Basic or Premium for more AI Studio features.'}
                {userTier === 'basic' && 'Upgrade to Premium for unlimited AI Studio access.'}
                {userTier === 'premium' && 'You have our top tier. Contact support if you need more.'}
              </p>
            </div>
            
            {userTier !== 'premium' && (
              <div className="grid gap-3">
                {userTier === 'free' && (
                  <Button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      navigate('/account');
                    }}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade to Basic - €7.99/mo
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/account');
                  }}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {userTier === 'free' ? 'Upgrade to Premium - €14.99/mo' : 'Upgrade to Premium - €14.99/mo'}
                </Button>
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Maybe Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIStudio;
